import type { PokerCgSimpleFilterVars } from '@ps/web-martech-cms-types';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useCMSReference } from '@swf/cms';
import { select } from '@swf/core/store';
import type { CashGamesGroupedQueryVariables } from '@swf/generated/graphql/poker/CG/operations';
import type { CashGameProperties, Currency } from '@swf/generated/graphql/poker/CG/schemas';

import { SITE_NAME_SITE_VARIABLE_MAP } from '../constants';
import { usePokerCashGamesWidget } from '../model';
import { Platform } from '../types';
import { extractPlatformConfig, filterConfigByPlayMoney } from '../utils';

const useConfigVars = () => {
  const { widgetSelect, widgetConfig } = usePokerCashGamesWidget();
  const activeGameType = useSelector(widgetSelect.getActiveSimpleGameType);
  const siteName = useSelector(select.cms.getSiteName);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const platform = isEmbedded ? Platform.Embedded : Platform.Web;

  const filterVariables = useCMSReference<PokerCgSimpleFilterVars>(
    widgetConfig.simple_view_filter_configuration[0]
  ) as PokerCgSimpleFilterVars;

  const {
    bb_multiplier_regular: bbMultiplierRegular,
    bb_multiplier_zoom: bbMultiplierZoom,
    table_sizes: tableSizes,
    table_types_group: { table_types: tableTypes },
    modular_blocks: modularBlocks
  } = filterVariables;

  const filteredConfig = useMemo(() => {
    const platformConfig = extractPlatformConfig(modularBlocks, platform);
    return filterConfigByPlayMoney(platformConfig, isPlayMoney);
  }, [isPlayMoney, modularBlocks, platform]);

  const baseVariables = useMemo(
    () => ({
      bbMultiplierRegular,
      bbMultiplierZoom,
      tableTypes,
      gameTypes: [activeGameType],
      currencies: (isPlayMoney ? ['CURRENCY_PLAY_MONEY'] : []) as Currency[],
      tableSizes: [tableSizes],
      sites: [SITE_NAME_SITE_VARIABLE_MAP[siteName]],
      active: true,
      cashGameProperties: ['CASH_GAME_PROPERTY_WEB_ALLOWED' as CashGameProperties]
    }),
    [
      tableSizes,
      tableTypes,
      bbMultiplierRegular,
      bbMultiplierZoom,
      siteName,
      activeGameType,
      isPlayMoney
    ]
  );

  const variables: CashGamesGroupedQueryVariables = useMemo(
    () => ({
      include: merge({}, baseVariables, filteredConfig),
      exclude: {
        currencies: (!isPlayMoney ? ['CURRENCY_PLAY_MONEY'] : []) as Currency[]
      }
    }),
    [baseVariables, filteredConfig, isPlayMoney]
  );

  return variables;
};

export default useConfigVars;
